import type { ApiClient } from "@luckymenu/api-client";
import {
	imageUrl,
	type PrinterState,
	type PrintFailureReason,
	type RealtimeEvent,
	uuidv7,
} from "@luckymenu/core";
import {
	type Block,
	type ClaimedPrintJob,
	encodeBlocks,
	encodeJob,
	forPrinter,
	logoBox,
	logoFiles,
	type MonoBitmap,
	type PaperWidth,
	PRINT_HEAD_START_MS,
	type PrintDocument,
	type Printer,
	type PrinterList,
	type PrinterStatus,
	type PrintJob,
	printModeFor,
	withLogos,
} from "@luckymenu/print-core";
import type { RealtimeClient } from "@luckymenu/realtime-client";
import type {
	DiscoveredPrinter,
	PrinterConnection,
	PrintTransport,
	StaffPlatform,
} from "../adapters";
import { decodeBitmap, encodeBitmap } from "./bitmap";
import {
	blockingState,
	errorDetail,
	isOffline,
	sendFailureReason,
	transportCodeOf,
} from "./errors";

/**
 * The print service (printing.md, Print jobs and stations; P5-T20 to
 * P5-T23, P5-T28): written once, used by both staff apps with their own
 * transports and rasteriser (`StaffPlatform.printTransports`, `rasterise`).
 *
 * - Picks the transport for a printer by its connection.
 * - Claims a job, says it starts sending (`startJob`), encodes it (text
 *   mode, or image mode through the rasteriser, logos dithered and cached),
 *   sends it and reports `completeJob` or `failJob`. Nothing is sent again
 *   by itself once bytes may have reached the paper.
 * - One local queue per printer, so this device prints one job at a time on
 *   each printer.
 * - As a print station (P5-T21): reports the printers it reaches on start
 *   and every minute, listens for `printjob.created` and `printjob.updated`,
 *   and claims; another device's job only after its 2 second head start
 *   (the server enforces it; this device tries again once it is over).
 * - Without the API (P5-T22): reports wait on the device and go when it is
 *   back; a reprint whose job cannot be made prints directly from the data
 *   on the device and is recorded later (`createJob` with `printedAt`).
 * - Opens the cash drawer on a cash payment (P5-T28).
 */

/** What this device last saw of a printer. */
export type PrinterReach = {
	reachable: boolean;
	state: PrinterState;
	/** `Date.now()` of the check. */
	checkedAt: number;
};

export type PrintServiceState = {
	/** Started for the active restaurant. */
	running: boolean;
	deviceId: string | null;
	/** By printer ID: only printers this device has a transport for. */
	reach: Record<string, PrinterReach>;
	/** By printer ID: this device prints that printer's jobs by itself (a print station). */
	autoPrint: Record<string, boolean>;
	/** By printer ID: the job this device is printing on it now. */
	printing: Record<string, string>;
	/** Results and offline prints waiting for the API. */
	pendingReports: number;
};

/** A report that could not reach the API yet (P5-T22). */
type PendingReport = { restaurantId: string } & (
	| { type: "complete"; jobId: string; deviceId: string }
	| {
			type: "fail";
			jobId: string;
			deviceId: string;
			reason: Exclude<PrintFailureReason, "may_have_printed">;
			detail?: string;
	  }
	| {
			type: "record";
			input: {
				id: string;
				kind: "bill" | "kot" | "test";
				refId: string;
				printerId: string;
				printedAt: string;
			};
	  }
);

export type CreateJobRequest = {
	kind: "bill" | "kot" | "test";
	refId: string;
	printerId?: string;
};

/** How a print the person asked for went. */
export type PrintOutcome =
	/** The job is made; this device or a station prints it. */
	| { kind: "queued"; job: PrintJob }
	/** No API: printed directly from this device, recorded when back online. */
	| { kind: "printedOffline" }
	/** No API and no printer this device reaches: nothing printed. */
	| { kind: "offline" };

const REPORT_EVERY_MS = 60_000;
const SWEEP_EVERY_MS = 30_000;
const REPORTS_KEY = "luckymenu.print.reports";
const autoPrintKey = (restaurantId: string) => `luckymenu.print.autoPrint.${restaurantId}`;
const logoKey = (fileId: string, paper: PaperWidth) => `luckymenu.print.logo.${fileId}.${paper}`;

function stateOf(status: PrinterStatus): PrinterState {
	return blockingState(status) ?? "ready";
}

export type PrintServiceDeps = {
	platform: StaffPlatform;
	client: ApiClient;
	/** This install's device ID (`luckymenu.deviceId`). */
	deviceId: () => Promise<string>;
	/** The restaurant API calls go to now. */
	restaurantId: () => string | null;
	realtime: RealtimeClient | null;
	now?: () => number;
};

export class PrintService {
	private readonly deps: PrintServiceDeps;
	private readonly now: () => number;
	private state: PrintServiceState = {
		running: false,
		deviceId: null,
		reach: {},
		autoPrint: {},
		printing: {},
		pendingReports: 0,
	};
	private readonly listeners = new Set<() => void>();
	private printers = new Map<string, Printer>();
	private billPrinterId: string | null = null;
	private readonly queues = new Map<string, Promise<void>>();
	private readonly logos = new Map<string, MonoBitmap | null>();
	private reports: PendingReport[] = [];
	private restaurantId: string | null = null;
	private stopFns: (() => void)[] = [];
	private generation = 0;
	private flushing: Promise<void> | null = null;

	constructor(deps: PrintServiceDeps) {
		this.deps = deps;
		this.now = deps.now ?? Date.now;
	}

	// ---- State for the screens ----

	getState = (): PrintServiceState => this.state;

	subscribe = (listener: () => void): (() => void) => {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	};

	private set(change: Partial<PrintServiceState>): void {
		this.state = { ...this.state, ...change };
		for (const listener of this.listeners) listener();
	}

	// ---- Printers and transports ----

	/** The transport for a printer's connection on this device, if any. */
	transportFor(printer: Pick<Printer, "connection">): PrintTransport | null {
		return (
			this.deps.platform.printTransports.find((t) => t.connection === printer.connection) ?? null
		);
	}

	/**
	 * Whether this device has a way to print on the printer at all. The
	 * browser print dialog belongs to the browser that set it up.
	 */
	canPrint(printer: Pick<Printer, "connection" | "address" | "isActive">): boolean {
		if (!printer.isActive || !this.transportFor(printer)) return false;
		if (printer.connection === "system") return printer.address === this.state.deviceId;
		return Boolean(printer.address);
	}

	/** This device prints the printer's jobs by itself: it can print there and the switch is on. */
	printsHereByItself(
		printer: Pick<Printer, "id" | "connection" | "address" | "isActive">,
	): boolean {
		return this.canPrint(printer) && this.autoPrintOf(printer);
	}

	/** Prints the printer's jobs by itself: on unless switched off here; off for the print dialog. */
	autoPrintOf(printer: Pick<Printer, "id" | "connection">): boolean {
		return this.state.autoPrint[printer.id] ?? printer.connection !== "system";
	}

	/**
	 * Fresh printers and assignments, from the screens' query or the service's
	 * own fetch. A printer new to this device is checked, unless the caller
	 * checks every printer next anyway (`probeNew: false`).
	 */
	setPrinterList(list: PrinterList, options: { probeNew?: boolean } = {}): void {
		const before = new Set(this.printers.keys());
		this.printers = new Map(list.printers.map((p) => [p.id, p]));
		this.billPrinterId = list.billPrinterId;
		const reach = Object.fromEntries(
			Object.entries(this.state.reach).filter(([id]) => this.printers.has(id)),
		);
		if (Object.keys(reach).length !== Object.keys(this.state.reach).length) this.set({ reach });
		// A printer new to this device: see whether it reaches it.
		if (this.state.running && options.probeNew !== false) {
			const added = list.printers.filter((p) => !before.has(p.id) && this.canPrint(p));
			if (added.length > 0) void this.probeAndReport();
		}
	}

	async refreshPrinters(options: { probeNew?: boolean } = {}): Promise<void> {
		this.setPrinterList(await this.deps.client.staff.printing.listPrinters.query(), options);
	}

	/**
	 * Printers the transports of this device find, likely receipt printers
	 * first. With a `connection`, only that transport searches and its
	 * failure is thrown (Bluetooth permission refused, not on Wi-Fi), so the
	 * add printer wizard can say why; without, one failing leaves the others.
	 */
	async discover(connection?: PrinterConnection): Promise<DiscoveredPrinter[]> {
		const found: DiscoveredPrinter[] = [];
		await Promise.all(
			this.deps.platform.printTransports.map(async (t) => {
				if (!t.discover) return;
				if (connection) {
					if (t.connection === connection) found.push(...(await t.discover()));
					return;
				}
				try {
					found.push(...(await t.discover()));
				} catch {
					// One transport failing (Bluetooth off, Print Bridge closed) leaves the others.
				}
			}),
		);
		return found.sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0) || a.name.localeCompare(b.name));
	}

	/** Asks the printer how it is. Unreachable throws; no answer is fine. */
	async check(printer: Printer): Promise<PrinterReach> {
		const transport = this.transportFor(printer);
		let reach: PrinterReach;
		if (!transport || !this.canPrint(printer)) {
			reach = { reachable: false, state: "unknown", checkedAt: this.now() };
		} else if (!transport.status || !printer.address || printer.connection === "system") {
			reach = { reachable: true, state: "unknown", checkedAt: this.now() };
		} else {
			try {
				const status = await transport.status(printer.address);
				reach = {
					reachable: true,
					state: status ? stateOf(status) : "unknown",
					checkedAt: this.now(),
				};
			} catch (error) {
				const code = transportCodeOf(error);
				reach =
					code === "unreachable" || code === "permission"
						? { reachable: false, state: "offline", checkedAt: this.now() }
						: { reachable: true, state: "unknown", checkedAt: this.now() };
			}
		}
		this.set({ reach: { ...this.state.reach, [printer.id]: reach } });
		return reach;
	}

	// ---- Station (P5-T21) ----

	/** Starts for the active restaurant: on sign-in, on a restaurant switch. */
	async start(): Promise<void> {
		this.stop();
		const generation = ++this.generation;
		const restaurantId = this.deps.restaurantId();
		if (!restaurantId) return;
		this.restaurantId = restaurantId;
		const { storage } = this.deps.platform;
		const deviceId = await this.deps.deviceId();
		const autoPrint = parseRecord(
			await storage.getItem(autoPrintKey(restaurantId)).catch(() => null),
		);
		this.reports = parseReports(await storage.getItem(REPORTS_KEY).catch(() => null));
		if (generation !== this.generation) return;
		this.set({
			running: true,
			deviceId,
			autoPrint,
			reach: {},
			printing: {},
			pendingReports: this.reports.length,
		});

		const realtime = this.deps.realtime;
		if (realtime) {
			this.stopFns.push(realtime.onEvent((event) => this.onEvent(event)));
			this.stopFns.push(
				realtime.onReconnect(() => {
					void this.flushReports();
					void this.sweep();
				}),
			);
		}
		const network = this.deps.platform.network;
		if (network) {
			this.stopFns.push(
				network.subscribe(() => {
					if (network.isOnline()) void this.flushReports();
				}),
			);
		}
		const report = setInterval(() => void this.probeAndReport(), REPORT_EVERY_MS);
		const sweep = setInterval(() => {
			void this.flushReports();
			void this.sweep();
		}, SWEEP_EVERY_MS);
		this.stopFns.push(() => {
			clearInterval(report);
			clearInterval(sweep);
		});

		try {
			// Every printer is checked just below: not once more as "new".
			await this.refreshPrinters({ probeNew: false });
		} catch {
			// Offline or no printing in the plan: the minute timer tries again.
		}
		if (generation !== this.generation) return;
		await this.flushReports();
		await this.probeAndReport();
		await this.sweep();
	}

	stop(): void {
		this.generation++;
		for (const fn of this.stopFns.splice(0)) fn();
		this.printers = new Map();
		this.billPrinterId = null;
		this.restaurantId = null;
		if (this.state.running) this.set({ running: false, reach: {}, printing: {} });
	}

	/** Checks every printer this device has a transport for, and tells the API (`reportStation`). */
	async probeAndReport(): Promise<void> {
		const generation = this.generation;
		if (this.printers.size === 0) {
			try {
				await this.refreshPrinters({ probeNew: false });
			} catch {
				return;
			}
		}
		const mine = [...this.printers.values()].filter(
			(p) => p.isActive && this.transportFor(p) !== null,
		);
		// A printer busy with this device's job is reachable; asking it now would wait for the job.
		const reaches = await Promise.all(
			mine.map((p) =>
				this.state.printing[p.id]
					? Promise.resolve(
							this.state.reach[p.id] ?? { reachable: true, state: "unknown" as const },
						)
					: this.check(p),
			),
		);
		const deviceId = this.state.deviceId;
		if (generation !== this.generation || !deviceId || mine.length === 0) return;
		try {
			await this.deps.client.staff.printing.reportStation.mutate({
				deviceId,
				deviceName: this.deps.platform.deviceName ?? null,
				printers: mine.slice(0, 20).map((p, i) => ({
					printerId: p.id,
					isReachable: reaches[i]?.reachable ?? false,
					state: reaches[i]?.state ?? "unknown",
					autoPrint: this.printsHereByItself(p),
				})),
			});
		} catch {
			// Reported again in a minute.
		}
	}

	/** Claims what is waiting on every printer this device prints by itself. */
	async sweep(): Promise<void> {
		await Promise.all(
			[...this.printers.values()]
				.filter((p) => this.isStationFor(p))
				.map((p) => this.claimAndPrint(p.id).catch(() => {})),
		);
	}

	private isStationFor(printer: Printer): boolean {
		return (
			this.canPrint(printer) &&
			this.autoPrintOf(printer) &&
			this.state.reach[printer.id]?.reachable !== false
		);
	}

	private onEvent(event: RealtimeEvent): void {
		if (event.type !== "printjob.created" && event.type !== "printjob.updated") return;
		const data = event.data as { printerId?: unknown; status?: unknown };
		if (typeof data.printerId !== "string") return;
		if (event.type === "printjob.updated" && data.status !== "queued") return;
		const printer = this.printers.get(data.printerId);
		if (!printer) {
			// A printer this device has not heard of yet.
			void this.refreshPrinters().catch(() => {});
			return;
		}
		if (!this.isStationFor(printer)) return;
		void this.claimAndPrint(printer.id).then((printed) => {
			// Another device's job: it has the first 2 seconds; then this station.
			if (printed === 0) {
				setTimeout(
					() => void this.claimAndPrint(printer.id).catch(() => {}),
					PRINT_HEAD_START_MS + 300,
				);
			}
		});
	}

	/** Prints on this device by itself from now on, or not (More > Printers). */
	async setAutoPrint(printerId: string, on: boolean): Promise<void> {
		const autoPrint = { ...this.state.autoPrint, [printerId]: on };
		this.set({ autoPrint });
		if (this.restaurantId) {
			await this.deps.platform.storage
				.setItem(autoPrintKey(this.restaurantId), JSON.stringify(autoPrint))
				.catch(() => {});
		}
		await this.probeAndReport();
		if (on) void this.claimAndPrint(printerId).catch(() => {});
	}

	// ---- Printing jobs (P5-T20) ----

	/** Runs `task` after the printer's earlier jobs on this device. */
	private enqueue<T>(printerId: string, task: () => Promise<T>): Promise<T> {
		const before = this.queues.get(printerId) ?? Promise.resolve();
		const run = before.then(task, task);
		const tail = run.then(
			() => {},
			() => {},
		);
		this.queues.set(printerId, tail);
		void tail.then(() => {
			if (this.queues.get(printerId) === tail) this.queues.delete(printerId);
		});
		return run;
	}

	/**
	 * Claims and prints the printer's waiting jobs one by one (`jobIds`: only
	 * those). Resolves to how many it printed or tried.
	 */
	claimAndPrint(printerId: string, jobIds?: string[]): Promise<number> {
		return this.enqueue(printerId, async () => {
			const deviceId = this.state.deviceId ?? (await this.deps.deviceId());
			const generation = this.generation;
			let handled = 0;
			let wanted = jobIds ? [...jobIds] : undefined;
			while (generation === this.generation) {
				if (wanted && wanted.length === 0) break;
				let claimed: ClaimedPrintJob[];
				try {
					claimed = await this.deps.client.staff.printing.claimJobs.mutate({
						deviceId,
						printerId,
						limit: 1,
						...(wanted ? { jobIds: wanted } : {}),
					});
				} catch {
					break;
				}
				const job = claimed[0];
				if (!job) break;
				handled++;
				wanted = wanted?.filter((id) => id !== job.id);
				await this.printClaimed(job, deviceId);
			}
			return handled;
		});
	}

	/** One claimed job: status, `startJob`, encode, send, report. */
	private async printClaimed(job: ClaimedPrintJob, deviceId: string): Promise<void> {
		const printer = { ...job.printer, stations: [] } satisfies Printer;
		const transport = this.transportFor(printer);
		const fail = (reason: Exclude<PrintFailureReason, "may_have_printed">, detail?: string) =>
			this.report({
				type: "fail",
				jobId: job.id,
				deviceId,
				reason,
				...(detail ? { detail } : {}),
			});
		if (!transport || !this.canPrint(printer) || !printer.address) {
			await fail("unreachable", "This device has no way to reach the printer");
			return;
		}
		this.set({ printing: { ...this.state.printing, [printer.id]: job.id } });
		try {
			// Status is advisory: only a definite problem stops the job.
			if (transport.status && printer.connection !== "system") {
				try {
					const status = await transport.status(printer.address);
					const blocked = status ? blockingState(status) : null;
					this.noteReach(printer.id, true, status ? stateOf(status) : "unknown");
					if (blocked) {
						await fail(blocked);
						return;
					}
				} catch (error) {
					if (transportCodeOf(error) === "unreachable") {
						this.noteReach(printer.id, false, "offline");
						await fail("unreachable", errorDetail(error));
						return;
					}
				}
			}

			let payload: { bytes: Uint8Array } | { blocks: Block[] };
			try {
				payload = await this.prepare(job.document, printer, transport);
			} catch (error) {
				await fail(
					error instanceof NoRasteriserError ? "no_rasteriser" : "other",
					errorDetail(error),
				);
				return;
			}

			// From here on an expired lease means "may have printed".
			try {
				await this.deps.client.staff.printing.startJob.mutate({ deviceId, jobId: job.id });
			} catch {
				// Not the holder any more, or no API: nothing was sent, and the
				// lease returns the job to the queue for a station that can report.
				return;
			}

			try {
				if ("blocks" in payload && transport.printDocument) {
					await transport.printDocument(payload.blocks, printer.paperWidthMm, documentTitle(job));
				} else if ("bytes" in payload) {
					await transport.send(printer.address, payload.bytes);
				}
			} catch (error) {
				const reason = sendFailureReason(error);
				if (reason === "unreachable") this.noteReach(printer.id, false, "offline");
				await fail(reason, errorDetail(error));
				return;
			}
			this.noteReach(printer.id, true, this.state.reach[printer.id]?.state ?? "unknown");
			await this.report({ type: "complete", jobId: job.id, deviceId });
		} finally {
			const { [printer.id]: _, ...printing } = this.state.printing;
			this.set({ printing });
		}
	}

	private noteReach(printerId: string, reachable: boolean, state: PrinterState): void {
		this.set({
			reach: { ...this.state.reach, [printerId]: { reachable, state, checkedAt: this.now() } },
		});
	}

	/** Bytes for a printer, or the resolved blocks for the print dialog. */
	private async prepare(
		document: Pick<PrintDocument, "blocks">,
		printer: Printer,
		transport: PrintTransport,
	): Promise<{ bytes: Uint8Array } | { blocks: Block[] }> {
		const paper = printer.paperWidthMm;
		await this.loadLogos(document.blocks, paper);
		const logo = (fileId: string) => this.logos.get(logoKey(fileId, paper)) ?? null;
		const profile = { paper, capabilities: printer.capabilities, mode: printer.mode };
		if (transport.printDocument) {
			return { blocks: forPrinter(withLogos(document.blocks, paper, logo), profile) };
		}
		const rasterise = this.deps.platform.rasterise;
		if (!rasterise) {
			const blocks = forPrinter(withLogos(document.blocks, paper, logo), profile);
			if (printModeFor({ blocks }, printer.mode) === "image") throw new NoRasteriserError();
		}
		const encoded = await encodeJob(document, profile, rasterise ?? undefined, { logo });
		return { bytes: encoded.bytes };
	}

	/** Reports a result, or keeps it for later when the API cannot be reached. */
	private async report(entry: DistributiveOmit<PendingReport, "restaurantId">): Promise<void> {
		const restaurantId = this.restaurantId ?? this.deps.restaurantId();
		if (!restaurantId) return;
		const full = { ...entry, restaurantId } as PendingReport;
		try {
			await this.send(full);
		} catch (error) {
			if (isOffline(error)) {
				this.reports.push(full);
				await this.saveReports();
			}
			// Otherwise the server refused it (not the holder any more): nothing to keep.
		}
	}

	private send(entry: PendingReport): Promise<unknown> {
		const printing = this.deps.client.staff.printing;
		switch (entry.type) {
			case "complete":
				return printing.completeJob.mutate({ deviceId: entry.deviceId, jobId: entry.jobId });
			case "fail":
				return printing.failJob.mutate({
					deviceId: entry.deviceId,
					jobId: entry.jobId,
					reason: entry.reason,
					...(entry.detail ? { detail: entry.detail } : {}),
				});
			case "record":
				return printing.createJob.mutate(entry.input);
		}
	}

	/** Sends the reports kept while offline, oldest first, for the active restaurant. */
	flushReports(): Promise<void> {
		this.flushing ??= (async () => {
			try {
				const restaurantId = this.deps.restaurantId();
				const left: PendingReport[] = [];
				let offline = false;
				for (const entry of this.reports) {
					if (offline || entry.restaurantId !== restaurantId) {
						left.push(entry);
						continue;
					}
					try {
						await this.send(entry);
					} catch (error) {
						// A refusal (not the holder any more, already recorded) is final.
						if (isOffline(error)) {
							offline = true;
							left.push(entry);
						}
					}
				}
				this.reports = left;
				await this.saveReports();
			} finally {
				this.flushing = null;
			}
		})();
		return this.flushing;
	}

	private async saveReports(): Promise<void> {
		this.set({ pendingReports: this.reports.length });
		await this.deps.platform.storage
			.setItem(REPORTS_KEY, JSON.stringify(this.reports))
			.catch(() => {});
	}

	// ---- What people ask for (P5-T26, P5-T22) ----

	/**
	 * Prints a bill, a KOT or a test page: makes the job (a "Duplicate" when
	 * printed before) and, when this device reaches the printer, prints it
	 * here at once (it has the head start). Without the API, a bill or KOT
	 * prints directly from `offline` (the document built on the device) on a
	 * printer this device reaches, and is recorded when the API is back.
	 */
	async print(
		request: CreateJobRequest,
		offline?: () => PrintDocument | null,
	): Promise<PrintOutcome> {
		const id = uuidv7();
		let job: PrintJob;
		try {
			job = await this.deps.client.staff.printing.createJob.mutate({ id, ...request });
		} catch (error) {
			if (!isOffline(error) || !offline) throw error;
			const printer = this.offlinePrinter(request);
			const document = printer ? offline() : null;
			if (!printer || !document) return { kind: "offline" };
			await this.printDirect(printer, document, { id, ...request, printerId: printer.id });
			return { kind: "printedOffline" };
		}
		const printer = this.printers.get(job.printerId);
		if (job.status === "queued" && printer && this.canPrint(printer)) {
			void this.claimAndPrint(printer.id, [job.id]).catch(() => {});
		}
		return { kind: "queued", job };
	}

	/** A printer this device can print on without the API: the one asked for, the bill printer, or the first. */
	private offlinePrinter(request: CreateJobRequest): Printer | null {
		const usable = (p: Printer | undefined) =>
			p && this.canPrint(p) && this.state.reach[p.id]?.reachable !== false ? p : null;
		if (request.printerId) return usable(this.printers.get(request.printerId));
		if (request.kind === "bill" && this.billPrinterId) {
			const bill = usable(this.printers.get(this.billPrinterId));
			if (bill) return bill;
		}
		return [...this.printers.values()].find((p) => usable(p)) ?? null;
	}

	private async printDirect(
		printer: Printer,
		document: PrintDocument,
		record: CreateJobRequest & { id: string; printerId: string },
	): Promise<void> {
		const transport = this.transportFor(printer);
		if (!transport || !printer.address) return;
		await this.enqueue(printer.id, async () => {
			const payload = await this.prepare(document, printer, transport);
			if ("blocks" in payload && transport.printDocument) {
				await transport.printDocument(payload.blocks, printer.paperWidthMm, document.kind);
			} else if ("bytes" in payload && printer.address) {
				await transport.send(printer.address, payload.bytes);
			}
		});
		await this.report({
			type: "record",
			input: {
				id: record.id,
				kind: record.kind,
				refId: record.refId,
				printerId: record.printerId,
				printedAt: new Date(this.now()).toISOString(),
			},
		});
	}

	/**
	 * Prints this device's own jobs for a bill or KOT at once, after the action
	 * that made them (finalise: "Print and close"), instead of waiting for the event.
	 */
	async printOwn(refType: "bill" | "kot", refId: string): Promise<void> {
		let jobs: PrintJob[];
		try {
			jobs = await this.deps.client.staff.printing.listJobs.query({
				refType,
				refId,
				status: ["queued"],
				limit: 20,
			});
		} catch {
			return;
		}
		const byPrinter = new Map<string, string[]>();
		for (const job of jobs) {
			const printer = this.printers.get(job.printerId);
			if (!printer || !this.canPrint(printer)) continue;
			byPrinter.set(job.printerId, [...(byPrinter.get(job.printerId) ?? []), job.id]);
		}
		await Promise.all(
			[...byPrinter].map(([printerId, ids]) => this.claimAndPrint(printerId, ids).catch(() => 0)),
		);
	}

	/** Retry: a failed or handled job waits again, and this device prints it if it can. */
	async retry(job: Pick<PrintJob, "id" | "printerId" | "status">): Promise<void> {
		if (job.status !== "queued") {
			await this.deps.client.staff.printing.retryJob.mutate({ jobId: job.id });
		}
		const printer = this.printers.get(job.printerId);
		if (printer && this.canPrint(printer)) await this.claimAndPrint(printer.id, [job.id]);
	}

	/** "Mark as handled": nobody prints it. */
	async markHandled(jobId: string): Promise<void> {
		await this.deps.client.staff.printing.cancelJob.mutate({ jobId });
	}

	/** Whether "Print from this device" is possible for the job's printer. */
	canPrintHere(printerId: string): boolean {
		const printer = this.printers.get(printerId);
		return Boolean(printer && this.canPrint(printer));
	}

	/**
	 * Opens the cash drawer of the bill printer on a cash payment (PR-10,
	 * P5-T28), when the printer has one (`capabilities.drawer`) and this
	 * device reaches it. Best effort: the payment is recorded either way.
	 */
	async openDrawer(): Promise<boolean> {
		const printer = this.billPrinterId ? this.printers.get(this.billPrinterId) : undefined;
		if (!printer?.capabilities.drawer || !this.canPrint(printer) || !printer.address) return false;
		const transport = this.transportFor(printer);
		if (!transport || transport.printDocument) return false;
		const address = printer.address;
		try {
			await this.enqueue(printer.id, () =>
				// Out of paper, the drawer still opens.
				transport.send(address, encodeBlocks([{ t: "drawer" }], printer.paperWidthMm), {
					statusCheck: false,
				}),
			);
			return true;
		} catch {
			return false;
		}
	}

	// ---- Logo (P5-T27) ----

	/**
	 * Dithers the restaurant's logo for both paper widths and keeps it on the
	 * device, so the first bill after an upload prints without waiting.
	 */
	async prepareLogo(fileId: string): Promise<void> {
		await this.loadLogos([{ t: "logo", fileId }], 58);
		await this.loadLogos([{ t: "logo", fileId }], 80);
	}

	private async loadLogos(blocks: Block[], paper: PaperWidth): Promise<void> {
		const { ditherImage, storage, cdnUrl } = this.deps.platform;
		for (const fileId of logoFiles(blocks)) {
			const key = logoKey(fileId, paper);
			if (this.logos.has(key)) continue;
			const stored = await storage.getItem(key).catch(() => null);
			const cached = stored ? decodeBitmap(stored) : null;
			if (cached) {
				this.logos.set(key, cached);
				continue;
			}
			if (!ditherImage) continue;
			const box = logoBox(paper);
			try {
				const bitmap = await ditherImage(imageUrl(cdnUrl, fileId, box.maxWidth * 2), box);
				this.logos.set(key, bitmap);
				await storage.setItem(key, encodeBitmap(bitmap)).catch(() => {});
			} catch {
				// No logo this time; the bill prints with the name alone.
			}
		}
	}
}

class NoRasteriserError extends Error {
	constructor() {
		super("Image mode needs a rasteriser on this device");
	}
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

function documentTitle(job: Pick<PrintJob, "kind" | "id">): string {
	return `${job.kind}-${job.id.slice(-6)}`;
}

function parseRecord(text: string | null): Record<string, boolean> {
	if (!text) return {};
	try {
		const raw = JSON.parse(text) as unknown;
		if (!raw || typeof raw !== "object") return {};
		return Object.fromEntries(
			Object.entries(raw).filter((e): e is [string, boolean] => typeof e[1] === "boolean"),
		);
	} catch {
		return {};
	}
}

function parseReports(text: string | null): PendingReport[] {
	if (!text) return [];
	try {
		const raw = JSON.parse(text) as unknown;
		return Array.isArray(raw)
			? raw.filter(
					(r): r is PendingReport =>
						Boolean(r) &&
						typeof r === "object" &&
						typeof (r as { restaurantId?: unknown }).restaurantId === "string" &&
						["complete", "fail", "record"].includes((r as { type?: string }).type ?? ""),
				)
			: [];
	} catch {
		return [];
	}
}
